const QUESTION_TYPES = [
  { value: "single_choice", label: "Escolha única" },
  { value: "multiple_choice", label: "Múltipla escolha" },
  { value: "open_text", label: "Texto aberto" },
];

const emptyQuestion = () => ({
  type: "single_choice",
  title: "",
  required: true,
  options: ["", ""],
});

export default {
  name: "SurveyForm",
  data() {
    return {
      uuid: this.$route.params.uuid || null,
      step: 1,
      loading: false,
      submitting: false,

      questionTypes: QUESTION_TYPES,

      // Tela 1 — Filtro de público
      audienceOptions: { user_types: [], subscription: [], plans: [] },
      filters: {
        user_type: "",
        created_from: "",
        created_to: "",
        last_appointment_from: "",
        last_appointment_to: "",
        without_appointments: false,
        subscription: "",
        plan_ids: [],
        approved: "",
      },
      previewLoading: false,
      previewCount: null,
      previewSample: [],
      previewTimer: null,

      // Tela 2 — Perguntas
      title: "",
      description: "",
      questions: [emptyQuestion()],
    };
  },
  computed: {
    isEditing() {
      return !!this.uuid;
    },
    isPsychologistAudience() {
      return this.filters.user_type === "psychologist";
    },
  },
  async mounted() {
    await this.loadAudienceOptions();
    if (this.uuid) {
      await this.loadExisting();
    }
    this.fetchPreview();
  },
  beforeDestroy() {
    clearTimeout(this.previewTimer);
  },
  methods: {
    async loadAudienceOptions() {
      try {
        const res = await this.$surveyAdminService.audienceOptions();
        this.audienceOptions = res.data || {};
      } catch (e) {
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: e.message || "Erro ao carregar opções de público.",
        });
      }
    },
    async loadExisting() {
      this.loading = true;
      try {
        const res = await this.$surveyAdminService.show(this.uuid);
        const survey = res.data;
        if (!survey.editable) {
          this.$eventBus.$emit("showToast", {
            title: "Não é possível editar",
            msg: "Essa pesquisa já foi enviada e não pode mais ser editada.",
          });
          this.$router.replace(`${this.$paths.surveys.detail}/${this.uuid}`);
          return;
        }
        this.title = survey.title;
        this.description = survey.description || "";
        this.filters = { ...this.filters, ...(survey.audience_filters || {}) };
        if (survey.questions?.length) {
          this.questions = survey.questions
            .slice()
            .sort((a, b) => a.position - b.position)
            .map((q) => ({
              type: q.type,
              title: q.title,
              required: !!q.required,
              options: q.options?.length ? [...q.options] : ["", ""],
            }));
        }
      } catch (e) {
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: e.message || "Erro ao carregar a pesquisa.",
        });
      } finally {
        this.loading = false;
      }
    },
    // without_appointments e last_appointment_* são mutuamente exclusivos
    // (doc: "se vierem os dois juntos, o primeiro vence") — a tela desabilita
    // um quando o outro está marcado, pra não mandar um filtro ambíguo.
    onWithoutAppointmentsChange() {
      if (this.filters.without_appointments) {
        this.filters.last_appointment_from = "";
        this.filters.last_appointment_to = "";
      }
      this.schedulePreview();
    },
    schedulePreview() {
      clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(() => this.fetchPreview(), 400);
    },
    normalizedFilters() {
      const f = { ...this.filters };
      if (!this.isPsychologistAudience) {
        delete f.subscription;
        delete f.plan_ids;
        delete f.approved;
      }
      const out = {};
      Object.keys(f).forEach((k) => {
        const v = f[k];
        if (v === "" || v === null || (Array.isArray(v) && v.length === 0)) return;
        out[k] = v;
      });
      return out;
    },
    async fetchPreview() {
      if (!this.filters.user_type) {
        this.previewCount = null;
        this.previewSample = [];
        return;
      }
      this.previewLoading = true;
      try {
        const res = await this.$surveyAdminService.audiencePreview(
          this.normalizedFilters()
        );
        this.previewCount = res.data?.count ?? 0;
        this.previewSample = res.data?.sample || [];
      } catch (e) {
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: e.message || "Erro ao calcular o público.",
        });
      } finally {
        this.previewLoading = false;
      }
    },
    togglePlan(planId) {
      const idx = this.filters.plan_ids.indexOf(planId);
      if (idx === -1) this.filters.plan_ids.push(planId);
      else this.filters.plan_ids.splice(idx, 1);
      this.schedulePreview();
    },
    goToQuestions() {
      this.step = 2;
    },
    backToAudience() {
      this.step = 1;
    },
    addQuestion() {
      this.questions.push(emptyQuestion());
    },
    removeQuestion(index) {
      this.questions.splice(index, 1);
    },
    addOption(qIndex) {
      this.questions[qIndex].options.push("");
    },
    removeOption(qIndex, oIndex) {
      this.questions[qIndex].options.splice(oIndex, 1);
    },
    // Um pouco redundante com a validação do backend, mas evita a viagem de
    // rede pra descobrir um erro que já dá pra ver na própria tela.
    questionError(q) {
      if (!q.title.trim()) return "Escreva o texto da pergunta.";
      if (q.type === "open_text") return null;
      const opts = q.options.map((o) => o.trim()).filter(Boolean);
      if (opts.length < 2) return "Adicione ao menos duas alternativas.";
      if (new Set(opts).size !== opts.length) return "Há alternativas repetidas.";
      return null;
    },
    canSubmit() {
      if (!this.title.trim()) return false;
      if (!this.questions.length) return false;
      return this.questions.every((q) => !this.questionError(q));
    },
    async submit() {
      if (!this.canSubmit()) return;
      this.submitting = true;
      try {
        const payload = {
          title: this.title,
          description: this.description,
          audience_filters: this.normalizedFilters(),
          questions: this.questions.map((q) => ({
            type: q.type,
            title: q.title,
            required: q.required,
            ...(q.type === "open_text"
              ? {}
              : { options: q.options.map((o) => o.trim()).filter(Boolean) }),
          })),
        };
        const res = this.uuid
          ? await this.$surveyAdminService.update(this.uuid, payload)
          : await this.$surveyAdminService.create(payload);
        const uuid = this.uuid || res.data.uuid;
        this.$eventBus.$emit("showToast", {
          title: "Rascunho salvo",
          msg: "A pesquisa foi salva como rascunho.",
        });
        this.$router.push(`${this.$paths.surveys.detail}/${uuid}`);
      } catch (e) {
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: e.message || "Erro ao salvar a pesquisa.",
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
